<template>
  <div class="today-wrong-container">
    <div class="practice-header">
      <div class="header-left">
        <i class="el-icon-arrow-left back-btn" @click="goBack"></i>
        <h1 class="practice-title">今日错题</h1>
      </div>
      <div class="header-right">
        <el-radio-group v-model="langMode" size="small" class="lang-toggle">
          <el-radio-button label="cn">中</el-radio-button>
          <el-radio-button label="en">英</el-radio-button>
        </el-radio-group>
        <el-button size="small" type="text" @click="goBack" class="back-amc-btn">返回课程</el-button>
      </div>
    </div>

    <div v-if="loading" class="loading">
      <div class="loading-inner">
        <i class="el-icon-loading" style="font-size:24px;margin-right:8px;"></i>
        <span>正在加载今日错题...</span>
      </div>
    </div>

    <div v-else-if="error" class="error">
      <div class="error-box">
        <i class="el-icon-warning"></i>
        <p>{{ error }}</p>
        <el-button type="primary" @click="reload">重新加载</el-button>
      </div>
    </div>

    <div v-else-if="sequenceMode && linearList && linearList.length" class="content">
      <div class="stats-section">
        <div class="stat-card">
          <div class="stat-number">{{ list.length }}</div>
          <div class="stat-label">今日错题数</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">
            {{ linearList[currentBlockIdx].type === 'wrong' ? (linearList[currentBlockIdx].idx + 1) : '-' }} / {{ list.length }}
          </div>
          <div class="stat-label">当前错题进度</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">{{ currentBlockIdx + 1 }} / {{ linearList.length }}</div>
          <div class="stat-label">当前卡片序号</div>
        </div>
      </div>

      <div class="question-card" v-if="linearList[currentBlockIdx]" :key="linearList[currentBlockIdx].key || currentBlockIdx">
        <div class="q-header">
          <div class="q-title">
            <template v-if="linearList[currentBlockIdx].type === 'wrong'">错题：第 {{ linearList[currentBlockIdx].idx + 1 }} / {{ total }} 题</template>
            <template v-else>相似题</template>
          </div>
          <div style="display:flex; align-items:center; gap:10px;">
            <el-radio-group v-model="langMode" size="mini">
              <el-radio-button label="cn">中</el-radio-button>
              <el-radio-button label="en">英</el-radio-button>
            </el-radio-group>
            <div class="q-meta" v-if="linearList[currentBlockIdx].type==='wrong' && linearList[currentBlockIdx].item">
              <span v-if="linearList[currentBlockIdx].item.knowledge">{{ resolveKnowledgeName(linearList[currentBlockIdx].item.knowledge) }}</span>
              <el-tag v-if="linearList[currentBlockIdx].item.difficulty" size="mini" :type="getDifficultyType(linearList[currentBlockIdx].item.difficulty)" style="margin-left: 8px;">
                难度 {{ formatDifficulty(linearList[currentBlockIdx].item.difficulty) }}
              </el-tag>
            </div>
            <div class="q-meta" v-else-if="linearList[currentBlockIdx].type==='similar'">
              <span v-if="linearList[currentBlockIdx].item.knowledge" style="margin-right: 8px;">{{ resolveKnowledgeName(linearList[currentBlockIdx].item.knowledge) }}</span>
              <el-tag size="mini" type="info">相似题</el-tag>
              <el-tag v-if="linearList[currentBlockIdx].item.number" size="mini">{{ linearList[currentBlockIdx].item.number }}</el-tag>
              <el-tag v-if="linearList[currentBlockIdx].item.year" size="mini" type="success">{{ linearList[currentBlockIdx].item.year }}</el-tag>
              <el-tag v-if="linearList[currentBlockIdx].item.difficulty" size="mini" :type="getDifficultyType(linearList[currentBlockIdx].item.difficulty)">难度 {{ formatDifficulty(linearList[currentBlockIdx].item.difficulty) }}</el-tag>
            </div>
          </div>
        </div>

        <div class="q-content" v-mathjax v-html="formatContent(linearList[currentBlockIdx].type==='wrong' ? getWrongContent(linearList[currentBlockIdx]) : getSimilarContent(linearList[currentBlockIdx].item))"></div>

        <!-- 题目图片 -->
        <div class="q-images" v-if="linearList[currentBlockIdx].type==='wrong' && getQuestionImages(linearList[currentBlockIdx]).length">
          <img v-for="(imgUrl, idx) in getQuestionImages(linearList[currentBlockIdx])" :key="idx" :src="imgUrl" class="question-image" @error="handleImageError" />
        </div>
        <div class="q-images" v-else-if="linearList[currentBlockIdx].type==='similar' && getSimilarImages(linearList[currentBlockIdx].item).length">
          <img v-for="(imgUrl, idx) in getSimilarImages(linearList[currentBlockIdx].item)" :key="idx" :src="imgUrl" class="question-image" @error="handleImageError" />
        </div>

        <template v-if="linearList[currentBlockIdx].type==='wrong'">
          <div class="q-options" v-if="getWrongOptions(linearList[currentBlockIdx]).length">
            <el-radio-group v-model="userAnswer" @change="onAnswerChange">
              <el-radio v-for="(opt, idx) in getWrongOptions(linearList[currentBlockIdx])" :key="idx" :label="getOptionKey(opt, idx)" class="opt">
                <div class="option-content">
                  <span class="option-label">{{ getOptionKey(opt, idx) }}. </span>
                  <!-- 如果有选项图片，只显示图片；否则显示文本 -->
                  <img v-if="getOptionImage(linearList[currentBlockIdx], idx)" :src="getOptionImage(linearList[currentBlockIdx], idx)" class="option-image" @error="handleImageError" />
                  <span v-else v-mathjax v-html="formatContent(getOptionText(opt))"></span>
                </div>
              </el-radio>
            </el-radio-group>
          </div>
          <div class="q-answer-input" v-else>
            <el-input v-model="userAnswer" placeholder="请输入答案" maxlength="20" style="width:240px" />
          </div>

          <div class="answer-status" v-if="hasAnswered">
            <div v-if="isCorrect" class="ok"><i class="el-icon-check"></i><span>回答正确</span></div>
            <div v-else class="bad"><i class="el-icon-close"></i><span>回答错误</span></div>
          </div>

          <div class="analysis" v-if="hasAnswered && displayAnalysis">
            <div class="a-title">解析</div>
            <div class="a-content" v-mathjax v-html="formatContent(displayAnalysis)"></div>
          </div>

          <div class="actions">
            <el-button
              v-if="!hasAnswered || !isCorrect"
              type="primary"
              :disabled="!userAnswer"
              :loading="submitting"
              @click="submitAnswer"
            >提交答案</el-button>
            <el-button
              v-else-if="isCorrect"
              type="success"
              :loading="loadingNext"
              @click="nextQuestion"
            >下一题</el-button>
          </div>
        </template>

        <template v-else>
          <div class="q-options" v-if="getSimilarOptions(linearList[currentBlockIdx].item).length">
            <el-radio-group v-model="userAnswer" @change="onAnswerChange">
              <el-radio v-for="(opt, oi) in getSimilarOptions(linearList[currentBlockIdx].item)" :key="oi" :label="getOptionKey(opt, oi)" class="opt">
                <span>{{ getOptionKey(opt, oi) }}. </span><span v-mathjax v-html="formatContent(getOptionText(opt))"></span>
              </el-radio>
            </el-radio-group>
          </div>
          <div class="q-answer-input" v-else>
            <el-input v-model="userAnswer" placeholder="请输入答案" maxlength="20" style="width:240px" />
          </div>

          <div class="answer-status" v-if="hasAnswered">
            <div v-if="isCorrect" class="ok"><i class="el-icon-check"></i><span>回答正确</span></div>
            <div v-else class="bad"><i class="el-icon-close"></i><span>回答错误</span></div>
          </div>

          <div class="analysis" v-if="hasAnswered && !isCorrect && getSimilarAnalysis(linearList[currentBlockIdx].item)">
            <div class="a-title">解析</div>
            <div class="a-content" v-mathjax v-html="formatContent(getSimilarAnalysis(linearList[currentBlockIdx].item))"></div>
          </div>

          <div class="actions" style="margin-top:12px;">
            <el-button
              v-if="!hasAnswered || !isCorrect"
              type="primary"
              :disabled="!userAnswer"
              :loading="submitting"
              @click="submitSimilar"
            >提交答案</el-button>
            <el-button
              v-else-if="isCorrect"
              type="success"
              :loading="loadingNext"
              @click="nextBlock"
            >下一题</el-button>
          </div>
        </template>
      </div>
    </div>

    <div v-else-if="cardMode && list && list.length" class="content">
      <div class="stats-section">
        <div class="stat-card">
          <div class="stat-number">{{ list.length }}</div>
          <div class="stat-label">今日错题数</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">{{ mixedList.length }}</div>
          <div class="stat-label">展示卡片数（含相似题）</div>
        </div>
      </div>

      <div class="question-card" v-for="(block, bi) in mixedList" :key="block.key || bi">
        <div class="q-header">
          <div class="q-title">
            <template v-if="block.type === 'wrong'">第 {{ block.idx + 1 }} / {{ total }} 题（错题）</template>
            <template v-else>相似题</template>
          </div>
          <div style="display:flex; align-items:center; gap:10px;">
            <el-radio-group v-model="langMode" size="mini">
              <el-radio-button label="cn">中</el-radio-button>
              <el-radio-button label="en">英</el-radio-button>
            </el-radio-group>
            <div class="q-meta" v-if="block.type==='wrong' && block.item">
              <span v-if="block.item.knowledge">{{ resolveKnowledgeName(block.item.knowledge) }}</span>
              <el-tag v-if="block.item.difficulty" size="mini" :type="getDifficultyType(block.item.difficulty)" style="margin-left: 8px;">
                难度 {{ formatDifficulty(block.item.difficulty) }}
              </el-tag>
            </div>
            <div class="q-meta" v-else-if="block.type==='similar'">
              <span v-if="block.item.knowledge" style="margin-right: 8px;">{{ resolveKnowledgeName(block.item.knowledge) }}</span>
              <el-tag size="mini" type="info">相似题</el-tag>
              <el-tag v-if="block.item.number" size="mini">{{ block.item.number }}</el-tag>
              <el-tag v-if="block.item.year" size="mini" type="success">{{ block.item.year }}</el-tag>
              <el-tag v-if="block.item.difficulty" size="mini" :type="getDifficultyType(block.item.difficulty)">难度 {{ formatDifficulty(block.item.difficulty) }}</el-tag>
            </div>
          </div>
        </div>

        <div class="q-content" v-html="formatContent(block.type==='wrong' ? getWrongContent(block) : getSimilarContent(block.item))"></div>

        <div class="q-options" v-if="block.type==='wrong' && getWrongOptions(block).length">
          <div v-for="(opt, idx) in getWrongOptions(block)" :key="idx" class="opt">
            {{ getOptionKey(opt, idx) }}. {{ getOptionText(opt) }}
          </div>
        </div>

        <div v-if="block.type==='similar' && getSimilarOptions(block.item).length" style="margin-top:6px;">
          <div v-for="(opt, oi) in getSimilarOptions(block.item)" :key="oi" class="opt" style="font-size:13px;">
            {{ getOptionKey(opt, oi) }}. {{ getOptionText(opt) }}
          </div>
        </div>
        <div v-if="block.type==='similar' && getSimilarAnswer(block.item)" style="margin-top:6px;color:#999;font-size:12px;">答案：{{ getSimilarAnswer(block.item) }}</div>
      </div>
    </div>

    <div v-else-if="current" class="content">
      <div class="stats-section">
        <div class="stat-card">
          <div class="stat-number">{{ total }}</div>
          <div class="stat-label">今日错题数</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">{{ currentIndex + 1 }} / {{ total }}</div>
          <div class="stat-label">当前错题进度</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">{{ answered }}</div>
          <div class="stat-label">已作答</div>
        </div>
      </div>

      <div class="question-card">
        <div class="q-header">
          <div class="q-title">第 {{ currentIndex + 1 }} / {{ total }} 题</div>
          <div style="display:flex; align-items:center; gap:10px;">
            <el-radio-group v-model="langMode" size="mini">
              <el-radio-button label="cn">中</el-radio-button>
              <el-radio-button label="en">英</el-radio-button>
            </el-radio-group>
            <div class="q-meta" v-if="current">
              <span v-if="current.knowledge">{{ current.knowledge }}</span>
              <el-tag v-if="current.difficulty" size="mini" :type="getDifficultyType(current.difficulty)" style="margin-left: 8px;">
                难度 {{ formatDifficulty(current.difficulty) }}
              </el-tag>
            </div>
          </div>
        </div>

        <div class="q-content" v-mathjax v-html="formatContent(displayContent)"></div>

        <div class="q-options" v-if="displayOptions && displayOptions.length">
          <el-radio-group v-model="userAnswer" @change="onAnswerChange">
            <el-radio
              v-for="(opt, idx) in displayOptions"
              :key="idx"
              :label="getOptionKey(opt, idx)"
              class="opt"
            >
              <span>{{ getOptionKey(opt, idx) }}. </span><span v-mathjax v-html="formatContent(getOptionText(opt))"></span>
            </el-radio>
          </el-radio-group>
        </div>

        <div class="q-answer-input" v-else>
          <el-input v-model="userAnswer" placeholder="请输入答案" maxlength="20" style="width:240px" />
        </div>

        <!-- 图片上传（可选，用于 AI 判题） -->
        <div class="upload-section">
          <div class="upload-title">上传做题图片（可选，用于AI判题）</div>
          <el-upload
            :action="uploadAction"
            :accept="'image/*'"
            :before-upload="handleBeforeUpload"
            :on-change="handleFileChange"
            :on-remove="handleRemoveImage"
            :multiple="false"
            :limit="1"
            :headers="uploadHeaders"
            :file-list="imageFileList"
            list-type="picture"
            :auto-upload="false"
          >
            <el-button size="small" type="primary">点击上传</el-button>
            <div slot="tip" class="el-upload__tip">仅支持图片格式，大小请控制在10MB内</div>
          </el-upload>
        </div>

        <div class="answer-status" v-if="hasAnswered">
          <div v-if="isCorrect" class="ok"><i class="el-icon-check"></i><span>回答正确</span></div>
          <div v-else class="bad"><i class="el-icon-close"></i><span>回答错误</span></div>
        </div>

        <div class="analysis" v-if="hasAnswered && displayAnalysis">
          <div class="a-title">解析</div>
          <div class="a-content" v-html="formatContent(displayAnalysis)"></div>
        </div>

        <div class="similar" v-if="currentSimilar && currentSimilar.length">
          <div class="a-title" style="display:flex;align-items:center;gap:8px;">
            <span>相似题目</span>
            <el-tag size="mini" type="info">相似题</el-tag>
          </div>
          <div class="similar-list">
            <div class="similar-item" v-for="(s, i) in currentSimilar" :key="s.id || i" style="padding:10px 12px;border:1px solid #eee;border-radius:8px;margin:8px 0;">
              <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;color:#666;">
                <el-tag size="mini" type="info">相似题</el-tag>
                <el-tag v-if="s.number" size="mini">{{ s.number }}</el-tag>
                <el-tag v-if="s.year" size="mini" type="success">{{ s.year }}</el-tag>
                <el-tag v-if="s.difficulty" size="mini" :type="getDifficultyType(s.difficulty)">难度 {{ formatDifficulty(s.difficulty) }}</el-tag>
              </div>
              <div class="s-content" v-html="formatContent(getSimilarContent(s))"></div>
              <div v-if="getSimilarOptions(s).length" style="margin-top:6px;">
                <div v-for="(opt, oi) in getSimilarOptions(s)" :key="oi" class="opt" style="font-size:13px;">
                  {{ getOptionKey(opt, oi) }}. {{ getOptionText(opt) }}
                </div>
              </div>
              <div v-if="getSimilarAnswer(s)" style="margin-top:6px;color:#999;font-size:12px;">答案：{{ getSimilarAnswer(s) }}</div>
            </div>
          </div>
        </div>

        <div class="actions">
          <el-button
            v-if="!hasAnswered || !isCorrect"
            type="primary"
            :disabled="!userAnswer"
            :loading="submitting"
            @click="submitAnswer"
          >提交答案</el-button>
          <el-button
            v-else-if="isCorrect"
            type="success"
            :loading="loadingNext"
            @click="nextQuestion"
          >下一题</el-button>
          <!-- <el-button size="mini" @click="testSimilar">测试相似题接口</el-button> -->
        </div>
      </div>
    </div>

    <div v-else class="empty">
      <div class="empty-box">
        <i class="el-icon-document"></i>
        <p>今日暂无错题</p>
        <el-button type="primary" @click="goBack">返回备考中心</el-button>
        <!-- <el-button type="primary" @click="test">测试每日计划</el-button> -->
      </div>
    </div>
    <!-- AI判题结果弹窗 -->
    <el-dialog
      title="AI判题结果"
      :visible.sync="showAiAnalysisDialog"
      width="600px"
      :before-close="() => { showAiAnalysisDialog = false }"
      :append-to-body="true"
    >
      <div class="ai-analysis-content" v-if="aiAnalysisResult">
        <div class="analysis-section">
          <h4 class="section-title">题目</h4>
          <div class="section-content">{{ aiAnalysisResult.question }}</div>
        </div>
        <div class="analysis-section">
          <h4 class="section-title">答案对比</h4>
          <div class="answer-comparison">
            <div class="answer-item">
              <span class="answer-label">你的答案：</span>
              <span class="answer-value user-answer">{{ aiAnalysisResult.user_answer }}</span>
            </div>
            <div class="answer-item">
              <span class="answer-label">正确答案：</span>
              <span class="answer-value correct-answer">{{ aiAnalysisResult.correct_answer }}</span>
            </div>
          </div>
        </div>
        <div class="analysis-section" v-if="aiAnalysisResult.ai_analysis">
          <h4 class="section-title">AI分析结果</h4>
          <div class="ai-result">
            <div class="result-item">
              <span class="result-label">判题结果：</span>
              <el-tag :type="aiAnalysisResult.ai_analysis.answer_correct ? 'success' : 'danger'" size="medium">
                {{ aiAnalysisResult.ai_analysis.answer_correct ? '正确' : '错误' }}
              </el-tag>
            </div>
            <div class="result-item" v-if="aiAnalysisResult.ai_analysis.step_analysis">
              <span class="result-label">步骤分析：</span>
              <div class="result-content">{{ aiAnalysisResult.ai_analysis.step_analysis }}</div>
            </div>
            <div class="result-item" v-if="aiAnalysisResult.ai_analysis.suggestion">
              <span class="result-label">改进建议：</span>
              <div class="result-content suggestion">{{ aiAnalysisResult.ai_analysis.suggestion }}</div>
            </div>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showAiAnalysisDialog = false">关闭</el-button>
        <el-button type="primary" @click="showAiAnalysisDialog = false">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { judgeSolution } from '@/api/ai'
import { getWrongRecordsByUserId, updateRecord, addRecord } from '@/api/userQuestionRecord'
import { getQuestionById, getSimilarQuestions } from '@/api/courseQuestion'
import { batchGetGeneralKnowledgeNames } from '@/api/knowledge'
import { getTokenInfo } from '@/utils/jwtUtils'
import { upsertTodayPlan, getProgressPaging, updateProgressFinished, getSchemeByUserIdAndRepoId, updateSchemeFinishedIds } from '@/api/studyPlan'
import axios from 'axios'

export default {
  name: 'TodayWrong',
  directives: {
    mathjax: {
      inserted(el) {
        try {
          if (window && window.MathJax && typeof window.MathJax.typesetPromise === 'function') {
            if (typeof window.MathJax.typesetClear === 'function') window.MathJax.typesetClear([el])
            window.MathJax.typesetPromise([el])
          }
        } catch (_) {}
      },
      componentUpdated(el) {
        try {
          if (window && window.MathJax && typeof window.MathJax.typesetPromise === 'function') {
            if (typeof window.MathJax.typesetClear === 'function') window.MathJax.typesetClear([el])
            window.MathJax.typesetPromise([el])
          }
        } catch (_) {}
      }
    }
  },
  data() {
    return {
      loading: false,
      loadingNext: false,
      submitting: false,
      error: null,
      list: [],
      current: null,
      currentDetail: null,
      currentIndex: 0,
      userAnswer: '',
      hasAnswered: false,
      isCorrect: false,
      answered: 0,
      // AI 判题
      showAiAnalysisDialog: false,
      aiAnalysisResult: null,
      // 语言
      langMode: 'cn',
      // 上传
      uploadAction: 'api/questions/uploadImage',
      uploadHeaders: {},
      imageFileList: [],
      uploadedImageFile: null,
      similarMap: {},
      similarLoading: false,
      cardMode: false,
      sequenceMode: true,
      currentBlockIdx: 0,
      mathReady: false,
      knowledgeNameMap: {}
      // 相似题不再要求连续做对，仅用于补充练习展示
    }
  },
  watch: {
    langMode() {
      this.typeset()
    },
    currentDetail() {
      this.typeset()
    },
    currentBlockIdx() {
      this.typeset()
    }
  },
  computed: {
    total() {
      return this.list.length
    },
    // 根据当前题的可用选项自动选择展示语种：若当前选择语种无选项而另一语种有，则切换到另一语种
    resolvedLang() {
      if (!this.currentDetail) return this.langMode
      const cnLen = (this.currentDetail.optionsCn || []).length
      const enLen = (this.currentDetail.optionsEn || []).length
      if (this.langMode === 'cn' && cnLen === 0 && enLen > 0) return 'en'
      if (this.langMode === 'en' && enLen === 0 && cnLen > 0) return 'cn'
      return this.langMode
    },
    displayContent() {
      if (!this.currentDetail) return ''
      if (this.resolvedLang === 'en') return this.currentDetail.contentEn || this.currentDetail.content || ''
      return this.currentDetail.contentCn || this.currentDetail.content || ''
    },
    displayOptions() {
      if (!this.currentDetail) return []
      const opts = this.resolvedLang === 'en' ? (this.currentDetail.optionsEn || []) : (this.currentDetail.optionsCn || [])
      return (opts && opts.length) ? opts : (this.currentDetail.options || [])
    },
    displayAnalysis() {
      if (!this.currentDetail) return ''
      if (this.resolvedLang === 'en') return this.currentDetail.analysisEn || this.currentDetail.analysis || ''
      return this.currentDetail.analysisCn || this.currentDetail.analysis || ''
    },
    currentSimilar() {
      if (!this.current) return []
      const id = this.current.quesId
      const arr = this.similarMap[id]
      return Array.isArray(arr) ? arr : []
    },
    mixedList() {
      const out = []
      const limit = 3
      this.list.forEach((w, idx) => {
        const keyW = `w-${w.quesId || w.id || idx}`
        out.push({ type: 'wrong', item: w, key: keyW, idx, detail: (this.currentDetail && this.current && this.current.quesId === w.quesId) ? this.currentDetail : null })
        const sims = Array.isArray(this.similarMap[w.quesId]) ? this.similarMap[w.quesId].slice(0, limit) : []
        sims.forEach((s, si) => {
          out.push({ type: 'similar', item: s, baseId: w.quesId, key: `s-${w.quesId}-${s.id || si}` })
        })
      })
      return out
    },
    linearList() {
      // 与 mixedList 相同，但渲染为单卡片导航
      return this.mixedList
    }
  },
  created() {
    this.init()
    this.ensureMathJax()
  },
  methods: {
    resolveKnowledgeName(val) {
      if (val == null || val === '') return ''
      const raw = String(val).trim()
      if (!raw) return ''
      if (!/^[0-9,，\s]+$/.test(raw)) return raw
      const ids = raw.split(/[，,\s]+/).filter(x => x)
      const names = ids
        .map(id => this.knowledgeNameMap[String(id)] || id)
        .filter(x => x != null && String(x).trim() !== '')
      return names.join('、')
    },
    async ensureKnowledgeNames(values) {
      try {
        const list = Array.isArray(values) ? values : [values]
        const ids = list
          .flatMap(v => (v == null ? [] : String(v).split(/[，,\s]+/)))
          .map(x => String(x).trim())
          .filter(x => x && /^\d+$/.test(x))
        const unique = Array.from(new Set(ids)).filter(id => !this.knowledgeNameMap[id])
        if (!unique.length) return
        const resp = await batchGetGeneralKnowledgeNames(unique)
        const data = resp?.data?.data ?? resp?.data ?? resp
        if (Array.isArray(data)) {
          data.forEach(item => {
            const id = item && (item.id ?? item.knowledgeId)
            const name = item && (item.content ?? item.name ?? item.title)
            if (id != null && name != null && String(name).trim() !== '') {
              this.$set(this.knowledgeNameMap, String(id), String(name))
            }
          })
        } else if (data && typeof data === 'object') {
          Object.keys(data).forEach(k => {
            this.$set(this.knowledgeNameMap, String(k), data[k])
          })
        }
      } catch (e) {
        console.error('获取知识点名称失败:', e)
      }
    },
    // 将今日学习进度的 reviewDone + 1（若存在当日记录）
    async incrementTodayReviewCount() {
      try {
        const user = getTokenInfo && getTokenInfo()
        const userId = user && user.id
        const repoId = this.$route && this.$route.query && this.$route.query.repoId
        const repoIdVal = repoId ? (Number(repoId) || repoId) : null
        if (!userId || !repoIdVal) return
        const todayStr = this.getTodayStr()
        const page = await getProgressPaging({ pageNum: 1, pageSize: 1, userId, repoId: repoIdVal, dateFrom: todayStr, dateTo: todayStr })
        const pdata = page?.data?.data ?? page?.data ?? {}
        const records = Array.isArray(pdata.records) ? pdata.records : []
        const first = records && records.length ? records[0] : null
        if (!first) return

        const progressId = first.id ?? first.progressId
        if (!progressId) return
        // 更新复习完成数量，将 reviewDone + 1
        const finishedId = first.finishedId ?? first.finished_id ?? ''
        const correctCount = first.correctCount ?? first.correct_count ?? 0
        const wrongCount = first.wrongCount ?? first.wrong_count ?? 0
        const currentReviewDone = first.reviewDone ?? first.review_done ?? 0
        const newReviewDone = Number(currentReviewDone) + 1
        await updateProgressFinished(progressId, finishedId, correctCount, wrongCount, newReviewDone)
      } catch (e) {
        console.error('更新复习数量失败:', e)
        // 静默失败，不阻断主流程
      }
    },
    getTodayStr() {
      const d = new Date()
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    ensureMathJax() {
      try {
        if (window && window.MathJax) {
          this.mathReady = true
          return
        }
      } catch (_) {}
      try {
        // 配置 MathJax v3（支持 $...$ 与 \(...\)）
        window.MathJax = {
          tex: {
            inlineMath: [['$', '$'], ['\\(', '\\)']],
            displayMath: [['$$','$$'], ['\\[','\\]']],
            processEscapes: true,
            processEnvironments: true
          },
          svg: { fontCache: 'global' },
          startup: {
            ready: () => {
              try { window.MathJax.startup.defaultReady() } catch (e) {}
              this.mathReady = true
              this.typeset()
            }
          }
        }
      } catch (_) {}
      const cdns = [
        'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-chtml.js',
        'https://unpkg.com/mathjax@3/es5/tex-chtml.js',
        'https://cdnjs.cloudflare.com/ajax/libs/mathjax/3.2.2/es5/tex-chtml.min.js'
      ]
      const loadFrom = (idx) => {
        if (idx >= cdns.length) return
        const script = document.createElement('script')
        script.type = 'text/javascript'
        script.src = cdns[idx]
        script.async = true
        script.onload = () => { this.mathReady = true; this.typeset() }
        script.onerror = () => { loadFrom(idx + 1) }
        document.head.appendChild(script)
      }
      loadFrom(0)
    },
    async typeset() {
      try {
        await this.$nextTick()
        if (window && window.MathJax && typeof window.MathJax.typesetPromise === 'function') {
          const scope = this.$el
          if (typeof window.MathJax.typesetClear === 'function') window.MathJax.typesetClear([scope])
          await window.MathJax.typesetPromise([scope])
        }
      } catch (_) {}
    },
    async test() {
      try {
        const date = '2025-11-07'
        const res = await upsertTodayPlan({ userId: 164, schemeId: 1, repoId: 14, date })
        console.log(res && (res.data ?? res))
        this.$message.success('upsertTodayPlan 调用成功')
      } catch (e) {
        console.error('upsertTodayPlan 调用失败:', e)
        this.$message.error('upsertTodayPlan 调用失败')
      }
    },
    async submitSimilar() {
      if (!this.linearList || !this.linearList[this.currentBlockIdx]) return
      const blk = this.linearList[this.currentBlockIdx]
      const s = blk.item
      if (!this.userAnswer) {
        this.$message.warning('请先选择或填写答案')
        return
      }
      const ua = String(this.userAnswer || '').trim().toUpperCase()
      const ca = String(this.getSimilarAnswer(s) || '').trim().toUpperCase()
      this.isCorrect = ua === ca
      this.hasAnswered = true

      // 写入做题记录（无论对错）
      try {
        const user = getTokenInfo && getTokenInfo()
        const userId = user && user.id
        const repoId = this.$route && this.$route.query && this.$route.query.repoId
        const repoIdVal = repoId ? (Number(repoId) || repoId) : null
        const quesId = s.id

        if (userId && repoIdVal && quesId) {
          // 1. 写入做题记录到 t_user_question_record
          const recordPayload = {
            userId,
            quesId,
            repoId: repoIdVal,
            quesContent: this.getSimilarContent(s) || '',
            userAnswer: this.userAnswer,
            answer: ca,
            judge: this.getSimilarAnalysis(s) || '',
            knowledge: s.knowledge || '',
            isRight: this.isCorrect ? 1 : 0,
            isReview: 0,
            time: new Date().toISOString()
          }
          await addRecord(recordPayload)

          // 2. 仅答对时更新 finished_id
          if (this.isCorrect) {
            // 更新 t_study_scheme.finished_id
            const schemeResp = await getSchemeByUserIdAndRepoId(userId, repoIdVal)
            const scheme = schemeResp?.data
            const schemeId = scheme ? (scheme.id ?? scheme.schemeId) : null

            if (schemeId) {
              const existing = scheme?.finishedId || ''
              const existSet = new Set(existing.split(/[，,\s]+/).filter(x => x).map(Number))
              existSet.add(Number(quesId))
              const merged = Array.from(existSet).filter(n => !isNaN(n) && n > 0).join(',')
              await updateSchemeFinishedIds(schemeId, merged)
            }

            // 更新 t_study_progress（当日进度的 finished_id 和 correct_count）
            const todayStr = this.getTodayStr()
            const page = await getProgressPaging({ pageNum: 1, pageSize: 1, userId, repoId: repoIdVal, dateFrom: todayStr, dateTo: todayStr })
            const pdata = page?.data?.data ?? page?.data ?? {}
            const records = Array.isArray(pdata.records) ? pdata.records : []
            const first = records && records.length ? records[0] : null

            if (first) {
              const progressId = first.id ?? first.progressId
              if (progressId) {
                const progressFinished = first.finishedId ?? first.finished_id ?? ''
                const progressSet = new Set(progressFinished.split(/[，,\s]+/).filter(x => x).map(Number))
                progressSet.add(Number(quesId))
                const progressMerged = Array.from(progressSet).filter(n => !isNaN(n) && n > 0).join(',')
                const correctCount = Number(first.correctCount ?? first.correct_count ?? 0) + 1
                const wrongCount = first.wrongCount ?? first.wrong_count ?? 0
                const reviewDone = first.reviewDone ?? first.review_done ?? 0
                await updateProgressFinished(progressId, progressMerged, correctCount, wrongCount, reviewDone)
              }
            }
          }
        }
      } catch (e) {
        console.error('写入相似题做题记录失败:', e)
      }

      if (this.isCorrect) {
        this.$message.success('回答正确！')
      } else {
        this.$message.error('回答错误，请查看解析')
      }

      this.typeset()
    },
    // 上传处理（与每日一题一致的交互）
    handleFileChange(file, fileList) {
      if (file.raw) {
        this.uploadedImageFile = file.raw
        this.imageFileList = fileList
      }
    },
    handleBeforeUpload() { return false },
    handleRemoveImage() {
      this.uploadedImageFile = null
      this.imageFileList = []
    },
    async init() {
      this.loading = true
      this.error = null
      try {
        const user = getTokenInfo && getTokenInfo()
        const userId = user && user.id
        if (!userId) throw new Error('未登录或用户信息缺失')

        // 获取 repoId 参数
        const repoId = this.$route && this.$route.query && this.$route.query.repoId
        const repoIdVal = repoId ? (Number(repoId) || repoId) : null

        // 调用 API 时传递 repoId
        const resp = await getWrongRecordsByUserId(userId, repoIdVal)
        const rows = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : [])

        console.log('[TodayWrong] 获取到错题记录:', rows.length, '条')

        // 只取"今天的且未复习"的错题：isReview === 0
        const todayRows = rows.filter(r => this.isToday(r.time || r.createTime || r.updateTime) && Number(r.isReview) === 0)

        console.log('[TodayWrong] 今日错题:', todayRows.length, '条')

        // 修复双语字段映射问题
        const mapped = todayRows.map(item => ({
          id: item.id,
          quesId: item.quesId,
          knowledge: item.knowledge || '',
          correctAnswer: item.correctAnswer || item.answer || '',
          // 支持双语字段：quescontentCn/En 或 contentCn/En
          quesContentCn: item.quescontentCn || item.contentCn || item.quesContent || item.content || '',
          quesContentEn: item.quescontentEn || item.contentEn || '',
          quesContent: item.quesContent || item.quescontentCn || item.contentCn || item.content || '',
          // 支持选项和选项图片
          questionOptions: item.questionOptions || item.options || '',
          optionsCn: item.optionsCn || item.questionOptions || item.options || '',
          optionsEn: item.optionsEn || '',
          optionImages: item.optionImages || null,
          images: item.images || null,
          image: item.image || null,
          imagePaths: item.imagePaths || null,
          // 解析字段
          judgeCn: item.judgeCn || item.analysisCn || item.judge || '',
          judgeEn: item.judgeEn || item.analysisEn || '',
          isReview: item.isReview,
          difficulty: item.difficulty || null
        }))
        this.list = mapped
        this.currentIndex = 0
        await this.loadCurrent()
        // 预加载相似题（顺序请求，避免压力过大）
        await this.prefetchSimilar()
        this.currentBlockIdx = 0
      } catch (e) {
        this.error = e?.message || '加载今日错题失败'
      } finally {
        this.loading = false
      }
    },
    goToBlock(idx) {
      if (!this.linearList || !this.linearList.length) return
      const i = Math.max(0, Math.min(idx, this.linearList.length - 1))
      this.currentBlockIdx = i
      const blk = this.linearList[i]
      if (blk && blk.type === 'wrong') {
        // 同步当前错题索引并加载详情
        const wrongIdx = this.list.findIndex(w => (w.quesId || w.id) === (blk.item.quesId || blk.item.id))
        if (wrongIdx >= 0) {
          this.currentIndex = wrongIdx
          this.loadCurrent()
        }
      } else {
        // 相似题：清空作答状态
        this.userAnswer = ''
        this.hasAnswered = false
        this.isCorrect = false
      }
      this.typeset()
    },
    nextBlock() {
      if (!this.linearList || this.currentBlockIdx + 1 >= this.linearList.length) {
        this.$message.success('今日错题已完成')
        this.goBack()
        return
      }
      this.loadingNext = true
      try {
        this.goToBlock(this.currentBlockIdx + 1)
      } finally {
        this.loadingNext = false
      }
    },
    async prefetchSimilar() {
      for (let i = 0; i < this.list.length; i += 1) {
        const q = this.list[i]
        if (!Array.isArray(this.similarMap[q.quesId])) {
          try { await this.loadSimilarFor(q.quesId, 3) } catch (_) {}
        }
      }
    },
    // 选项内容（含字母+文本）
    getFullOptionContent(optionKey) {
      const key = String(optionKey || '').trim().toUpperCase()
      if (!key || !(this.displayOptions && Array.isArray(this.displayOptions))) return key
      const idx = key.charCodeAt(0) - 65
      const opt = this.displayOptions[idx]
      const text = this.getOptionText(opt)
      return text ? `${key} ${text}` : key
    },

    // 处理 AI 判题响应并弹窗
    handleJudgeQuestionResponse(apiResponse) {
      try {
        const data = apiResponse && (apiResponse.data || apiResponse)
        const jr = data && (data.judgeResult || data)
        if (!jr) {
          this.$message.warning('AI判题结果为空')
          return
        }
        const result = {
          question: (this.currentDetail && this.currentDetail.content) || '',
          user_answer: this.getFullOptionContent(this.userAnswer),
          correct_answer: this.getFullOptionContent(this.currentDetail && this.currentDetail.answer),
          ai_analysis: jr.ai_analysis || null,
          status: jr.status || 'ok'
        }
        this.showJudgeResultDialog(result)
      } catch (e) {
        this.$message.warning('解析AI判题结果失败')
      }
    },

    // 显示 AI 判题结果弹窗
    showJudgeResultDialog(judgeResult) {
      this.aiAnalysisResult = judgeResult
      this.showAiAnalysisDialog = true
      if (judgeResult && judgeResult.ai_analysis) {
        const ok = !!judgeResult.ai_analysis.answer_correct
        this.$message[ok ? 'success' : 'error'](ok ? 'AI判定：正确' : 'AI判定：错误')
      }
    },
    isToday(ts) {
      if (!ts || ts === '-') return false
      const d = new Date(ts)
      if (isNaN(d.getTime())) return false
      const now = new Date()
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()
    },
    async loadCurrent() {
      this.current = this.list[this.currentIndex] || null
      this.currentDetail = null
      this.userAnswer = ''
      this.hasAnswered = false
      this.isCorrect = false
      if (!this.current) return
      let options = []
      try {
        if (this.current.questionOptions) {
          try {
            const parsed = JSON.parse(this.current.questionOptions)
            if (Array.isArray(parsed)) options = parsed
          } catch (_) {
            // 若为字符串形式，如 "(A)0;(B)1;..."，走自定义解析
            if (typeof this.current.questionOptions === 'string') {
              options = this.parseOptions(this.current.questionOptions)
            }
          }
        }
        let content = this.current.quesContent || ''
        let answer = this.current.correctAnswer || ''
        let contentCn = ''; let contentEn = ''; let analysisCn = ''; let analysisEn = ''
        let optionsCn = []; let optionsEn = []
        if (!content || options.length === 0) {
          const resp = await getQuestionById(this.current.quesId)
          const q = resp?.data || resp
          // 解析中英文选项
          const rawCn = q?.optionsCn || q?.options_cn || null
          const rawEn = q?.optionsEn || q?.options_en || null
          let rawOptions = q?.optionsStr || q?.options || q?.choices || q?.optionsList || []
          if (typeof rawOptions === 'string') {
            // 优先按字符串解析，兼容 "(A)xxx;(B)yyy" 或 "A. xxx;B. yyy" 等
            const normalized = this.normalizeOptions(rawOptions)
            options = this.parseOptions(normalized)
          } else {
            options = Array.isArray(rawOptions) ? rawOptions : []
          }
          if (typeof rawCn === 'string') optionsCn = this.parseOptions(this.normalizeOptions(rawCn))
          if (typeof rawEn === 'string') optionsEn = this.parseOptions(this.normalizeOptions(rawEn))

          contentCn = q?.contentCn || q?.contentcn || ''
          contentEn = q?.contentEn || q?.contenten || ''
          content = q?.content || q?.question || contentCn || contentEn || content || ''
          answer = q?.answer || q?.correctAnswer || answer || ''
          analysisCn = q?.analysisCn || q?.analysiscn || ''
          analysisEn = q?.analysisEn || q?.analysisen || ''
          const analysis = q?.analysis || q?.explain || q?.solution || ''
          this.currentDetail = { id: this.current.quesId, content, contentCn, contentEn, options, optionsCn, optionsEn, answer, analysis, analysisCn, analysisEn }
        } else {
          this.currentDetail = { id: this.current.quesId, content, contentCn: '', contentEn: '', options, optionsCn: [], optionsEn: [], answer, analysis: '', analysisCn: '', analysisEn: '' }
        }
      } catch (e) {
        this.currentDetail = { id: this.current.quesId, content: this.current.quesContent || '', contentCn: '', contentEn: '', options: [], optionsCn: [], optionsEn: [], answer: this.current.correctAnswer || '', analysis: '', analysisCn: '', analysisEn: '' }
      }
      try {
        await this.loadSimilarFor(this.current.quesId)
      } catch (_) {}
      this.typeset()
    },
    async loadSimilarFor(quesId, limit = 3) {
      if (!quesId) return
      if (Array.isArray(this.similarMap[quesId])) return
      this.similarLoading = true
      try {
        // 获取当前用户ID，用于智能推荐
        const user = getTokenInfo && getTokenInfo()
        const userId = user && user.id

        // 调用相似题接口，传入userId启用智能推荐
        // 推荐策略：优先推荐没做过的知识点相关题目，其次是做错的，最后是其他题目
        const resp = await getSimilarQuestions(quesId, limit, userId)
        const arr = resp?.data || []
        this.$set(this.similarMap, quesId, Array.isArray(arr) ? arr : [])
        try {
          const vals = (Array.isArray(arr) ? arr : []).map(it => it && it.knowledge).filter(v => v != null && String(v).trim() !== '')
          await this.ensureKnowledgeNames(vals)
        } catch (_) {}
      } catch (e) {
        console.error('加载相似题失败:', e)
        this.$set(this.similarMap, quesId, [])
      } finally {
        this.similarLoading = false
      }
    },
    // 规范化选项字符串，将 "A. xxx" 等转换为 "(A) xxx" 便于统一解析
    normalizeOptions(raw) {
      if (!raw) return ''
      let s = String(raw).trim()
      s = s
        .replace(/(^|[;\n\r\t\s])A\s*\.?\s*/g, '$1(A) ')
        .replace(/(^|[;\n\r\t\s])B\s*\.?\s*/g, '$1(B) ')
        .replace(/(^|[;\n\r\t\s])C\s*\.?\s*/g, '$1(C) ')
        .replace(/(^|[;\n\r\t\s])D\s*\.?\s*/g, '$1(D) ')
        .replace(/(^|[;\n\r\t\s])E\s*\.?\s*/g, '$1(E) ')
      return s
    },
    // 解析 "(A) xxx;(B) yyy" 为数组 [{content, sort}]
    parseOptions(optionsStr) {
      if (!optionsStr) return []
      const s = this.normalizeOptions(String(optionsStr))
      const results = []
      const regex = /\(([A-E])\)\s*([^;\n\r]+)/g
      let m
      while ((m = regex.exec(s)) !== null) {
        const key = (m[1] || '').toUpperCase()
        const value = (m[2] || '').trim()
        const sort = key.charCodeAt(0) - 64 // A->1
        results.push({ content: value, sort })
      }
      return results
    },
    getOptionKey(opt, idx) {
      if (opt && typeof opt.sort === 'number') return String.fromCharCode(64 + opt.sort)
      return String.fromCharCode(65 + idx)
    },
    getOptionText(opt) {
      if (!opt) return ''
      return (opt.content || opt.text || opt.label || '').toString()
    },
    onAnswerChange() {},
    formatContent(text) {
      if (!text) return ''
      return String(text).replace(/\n/g, '<br>')
    },
    getSimilarContent(s) {
      if (!s) return ''
      if (this.resolvedLang === 'en') return s.contentEn || s.content || s.question || s.contentCn || ''
      return s.contentCn || s.content || s.question || s.contentEn || ''
    },
    getSimilarOptions(s) {
      if (!s) return []
      let raw = null
      if (this.resolvedLang === 'en') raw = s.optionsEn || s.options || s.optionsStr || null
      else raw = s.optionsCn || s.options || s.optionsStr || null
      if (!raw && s.optionsEn) raw = s.optionsEn
      if (!raw && s.optionsCn) raw = s.optionsCn
      if (!raw) return []
      if (Array.isArray(raw)) return raw
      if (typeof raw === 'string') {
        const normalized = this.normalizeOptions(raw)
        return this.parseOptions(normalized)
      }
      return []
    },
    getSimilarAnswer(s) {
      if (!s) return ''
      return s.answer || s.correctAnswer || ''
    },
    getSimilarAnalysis(s) {
      if (!s) return ''
      if (this.resolvedLang === 'en') return s.analysisEn || s.analysis || s.explain || ''
      return s.analysisCn || s.analysis || s.explain || ''
    },
    getWrongContent(block) {
      const d = block && block.detail
      if (d) {
        if (this.resolvedLang === 'en') return d.contentEn || d.content || ''
        return d.contentCn || d.content || ''
      }
      const i = block && block.item
      if (!i) return ''
      // 支持多种双语字段格式
      if (this.resolvedLang === 'en') {
        return i.quesContentEn || i.contentEn || i.quesContent || i.content || ''
      }
      return i.quesContentCn || i.contentCn || i.quesContent || i.content || ''
    },
    getWrongOptions(block) {
      const d = block && block.detail
      if (d) {
        if (this.resolvedLang === 'en' && Array.isArray(d.optionsEn) && d.optionsEn.length) return d.optionsEn
        if (this.resolvedLang === 'cn' && Array.isArray(d.optionsCn) && d.optionsCn.length) return d.optionsCn
        if (Array.isArray(d.options) && d.options.length) return d.options
      }
      const i = block && block.item
      if (!i) return []

      // 支持多种双语选项字段格式
      let raw = null
      if (this.resolvedLang === 'en') {
        raw = i.optionsEn || i.questionOptions || i.options || i.optionsStr || i.optionsCn
      } else {
        raw = i.optionsCn || i.questionOptions || i.options || i.optionsStr
      }

      if (!raw) return []
      if (Array.isArray(raw)) return raw
      if (typeof raw === 'string') return this.parseOptions(this.normalizeOptions(raw))
      return []
    },
    getWrongAnswer(block) {
      const d = block && block.detail
      if (d && d.answer) return d.answer
      const i = block && block.item
      return (i && (i.correctAnswer || i.answer)) || ''
    },
    getQuestionImages(block) {
      const d = block && block.detail
      const i = block && block.item
      const images = []

      // 从 detail 或 item 中获取图片路径（优先处理 image 字段）
      const imageField = (d && d.image) || (i && i.image)
      const imagePaths = (d && d.imagePaths) || (i && i.imagePaths) || (i && i.images)

      // 优先解析 image 字段（可能是 JSON 字符串或数组）
      if (imageField) {
        try {
          const paths = typeof imageField === 'string' ? JSON.parse(imageField) : imageField
          if (Array.isArray(paths)) {
            paths.forEach(path => {
              if (path) images.push(this.getImageUrl(path))
            })
            return images
          } else if (typeof paths === 'string' && paths.trim()) {
            images.push(this.getImageUrl(paths))
            return images
          }
        } catch (e) {
          // 如果解析失败，尝试作为单个路径处理
          if (typeof imageField === 'string' && imageField.trim()) {
            images.push(this.getImageUrl(imageField))
            return images
          }
        }
      }

      // 解析 imagePaths（可能是 JSON 字符串或数组）
      if (imagePaths) {
        try {
          const paths = typeof imagePaths === 'string' ? JSON.parse(imagePaths) : imagePaths
          if (Array.isArray(paths)) {
            paths.forEach(path => {
              if (path) images.push(this.getImageUrl(path))
            })
          }
        } catch (e) {
          // 如果解析失败，尝试作为单个路径处理
          if (typeof imagePaths === 'string' && imagePaths.trim()) {
            images.push(this.getImageUrl(imagePaths))
          }
        }
      }

      return images
    },
    getSimilarImages(item) {
      if (!item) return []
      const images = []

      const imageField = item.image
      const imagePaths = item.imagePaths || item.images

      // 优先解析 image 字段
      if (imageField) {
        try {
          const paths = typeof imageField === 'string' ? JSON.parse(imageField) : imageField
          if (Array.isArray(paths)) {
            paths.forEach(path => {
              if (path) images.push(this.getImageUrl(path))
            })
            return images
          } else if (typeof paths === 'string' && paths.trim()) {
            images.push(this.getImageUrl(paths))
            return images
          }
        } catch (e) {
          if (typeof imageField === 'string' && imageField.trim()) {
            images.push(this.getImageUrl(imageField))
            return images
          }
        }
      }

      // 解析 imagePaths
      if (imagePaths) {
        try {
          const paths = typeof imagePaths === 'string' ? JSON.parse(imagePaths) : imagePaths
          if (Array.isArray(paths)) {
            paths.forEach(path => {
              if (path) images.push(this.getImageUrl(path))
            })
          }
        } catch (e) {
          if (typeof imagePaths === 'string' && imagePaths.trim()) {
            images.push(this.getImageUrl(imagePaths))
          }
        }
      }

      return images
    },
    getImageUrl(path) {
      if (!path) return ''
      // 如果已经是完整URL，直接返回
      if (path.startsWith('http://') || path.startsWith('https://')) {
        return path
      }
      // 转换为后端预览接口URL（使用 path 参数，与智能练习保持一致）
      const normalizedPath = path.startsWith('/') ? path : `/${path}`
      return `/api/file/preview?path=${encodeURIComponent(normalizedPath)}`
    },
    handleImageError(event) {
      console.error('图片加载失败:', event.target.src)
      event.target.style.display = 'none'
    },
    getOptionImage(block, optionIndex) {
      const d = block && block.detail
      const i = block && block.item

      // 从 detail 或 item 中获取选项图片
      const optionImages = (d && d.optionImages) || (i && i.optionImages)

      if (!optionImages) return null

      // optionImages 是分号分隔的路径列表
      const images = typeof optionImages === 'string' ? optionImages.split(';') : []

      if (optionIndex >= 0 && optionIndex < images.length) {
        const path = images[optionIndex].trim()
        return path ? this.getImageUrl(path) : null
      }

      return null
    },
    async submitAnswer() {
      if (!this.currentDetail) return
      if (!this.userAnswer) {
        this.$message.warning('请先选择或填写答案')
        return
      }
      this.submitting = true
      let loadingInstance = null
      try {
        try {
          loadingInstance = this.$loading({ lock: true, text: 'AI分析中...', spinner: 'el-icon-loading', background: 'rgba(0,0,0,0.5)' })
        } catch (e) {}

        let finalCorrect = false
        if (this.uploadedImageFile) {
          // 使用 AI 判题
          try {
            const formData = new FormData()
            formData.append('question', this.currentDetail.content || '')
            formData.append('user_answer', this.getFullOptionContent(this.userAnswer))
            formData.append('correct_answer', this.getFullOptionContent(this.currentDetail.answer))
            formData.append('explanation', this.currentDetail.analysis || '')
            formData.append('image', this.uploadedImageFile, this.uploadedImageFile.name)
            const response = await judgeSolution(formData)
            this.handleJudgeQuestionResponse(response)
            const aiCorrect = this.aiAnalysisResult && this.aiAnalysisResult.ai_analysis
              ? !!this.aiAnalysisResult.ai_analysis.answer_correct
              : null
            if (aiCorrect === null) {
              // 回退本地
              const ua = String(this.userAnswer || '').trim().toUpperCase()
              const ca = String(this.currentDetail.answer || '').trim().toUpperCase()
              finalCorrect = ua === ca
            } else {
              finalCorrect = aiCorrect
            }
          } catch (e) {
            this.$message.warning('AI判题失败，使用本地判定')
            const ua = String(this.userAnswer || '').trim().toUpperCase()
            const ca = String(this.currentDetail.answer || '').trim().toUpperCase()
            finalCorrect = ua === ca
          }
        } else {
          // 本地判定
          const ua = String(this.userAnswer || '').trim().toUpperCase()
          const ca = String(this.currentDetail.answer || '').trim().toUpperCase()
          finalCorrect = ua === ca
        }

        this.isCorrect = finalCorrect
        if (!this.hasAnswered) {
          this.answered += 1
        }
        this.hasAnswered = true

        if (finalCorrect) {
          // 错题订正后：直接标记 isReview=1
          try {
            const user = getTokenInfo && getTokenInfo()
            const userId = user && user.id
            const wrongItem = this.current
            const payload = {
              id: wrongItem && wrongItem.id,
              userId,
              quesId: wrongItem && wrongItem.quesId,
              quesContent: (wrongItem && (wrongItem.quesContentCn || wrongItem.quesContent)) || (this.currentDetail && (this.currentDetail.contentCn || this.currentDetail.content)) || '',
              isReview: 1
            }
            if (payload.id && payload.userId && payload.quesId) {
              await updateRecord(payload)
              await this.incrementTodayReviewCount()
              this.$message.success('订正完成：已标记为已复习')
            } else {
              this.$message.success('回答正确！')
            }
          } catch (e) {
            console.error('更新错题复习状态失败:', e)
            this.$message.success('回答正确！')
          }
        } else this.$message.error(`回答错误，正确答案是：${this.currentDetail.answer}`)
      } finally {
        if (loadingInstance && typeof loadingInstance.close === 'function') loadingInstance.close()
        this.submitting = false
      }
      this.typeset()
    },
    async nextQuestion() {
      if (this.sequenceMode) {
        // 在单题序列模式中，下一题跳到下一个区块（可能是相似题或下一错题）
        this.nextBlock()
        return
      }
      if (this.currentIndex + 1 >= this.total) {
        this.$message.success('今日错题已完成')
        this.goBack()
        return
      }
      this.loadingNext = true
      try {
        this.currentIndex += 1
        await this.loadCurrent()
      } finally {
        this.loadingNext = false
      }
    },
    async testSimilar() {
      try {
        const id = 333
        const limit = 3
        const resp = await findSimilar(id, limit)
        const arr = resp?.data?.data ?? resp?.data ?? []
        const count = Array.isArray(arr) ? arr.length : 0
        this.$message.success(`已调用 findSimilar(id=${id}, limit=${limit})，返回：${count} 条`)
        // eslint-disable-next-line no-console
        console.log('[TodayWrong] 相似题结果 =', arr)
      } catch (e) {
        this.$message.error('相似题接口调用失败')
      }
    },
    reload() {
      this.init()
    },
    goBack() {
      // 获取路由参数，返回当前课程主页
      const query = this.$route && this.$route.query ? this.$route.query : {}
      const repoId = query.repoId
      const level = query.level
      const courseTypeId = query.courseTypeId
      const courseTypeCode = query.courseTypeCode

      // 构建返回路径，携带课程参数
      this.$router.push({
        path: '/amc-course',
        query: {
          ...(repoId ? { repoId } : {}),
          ...(level ? { level } : {}),
          ...(courseTypeId ? { courseTypeId } : {}),
          ...(courseTypeCode ? { courseTypeCode } : {})
        }
      })
    },
    // 将难度数字或文字转换为统一的中文描述
    // 1-6: 入门, 7-12: 简单, 13-18: 一般, 19-25: 困难
    formatDifficulty(difficulty) {
      if (!difficulty) return '-'

      // 如果是数字或数字字符串
      const num = Number(difficulty)
      if (!isNaN(num)) {
        if (num >= 1 && num <= 6) return '入门'
        if (num >= 7 && num <= 12) return '简单'
        if (num >= 13 && num <= 18) return '一般'
        if (num >= 19 && num <= 25) return '困难'
        return difficulty // 超出范围则显示原值
      }

      // 如果已经是文字描述，直接返回
      return difficulty
    },

    // 获取难度标签类型
    getDifficultyType(difficulty) {
      if (!difficulty) return 'info'

      // 如果是数字或数字字符串，根据范围返回类型
      const num = Number(difficulty)
      if (!isNaN(num)) {
        if (num >= 1 && num <= 6) return 'success' // 入门-绿色
        if (num >= 7 && num <= 12) return '' // 简单-默认蓝色
        if (num >= 13 && num <= 18) return 'warning' // 一般-橙色
        if (num >= 19 && num <= 25) return 'danger' // 困难-红色
        return 'info'
      }

      // 如果是文字，根据关键词判断
      const d = String(difficulty).toLowerCase()
      if (d.includes('入门') || d.includes('easy') || d.includes('容易')) return 'success'
      if (d.includes('简单') || d.includes('simple')) return ''
      if (d.includes('一般') || d.includes('medium') || d.includes('中等') || d.includes('中')) return 'warning'
      if (d.includes('困难') || d.includes('hard') || d.includes('难')) return 'danger'
      return 'info'
    }
  }
}
</script>

<style scoped>
/* 整体容器 */
.today-wrong-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 20px;
}

/* 顶部导航栏 */
.practice-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 30px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 25px;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.back-btn {
  font-size: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #667eea;
  padding: 5px;
  border-radius: 50%;
}

.back-btn:hover {
  background: rgba(102, 126, 234, 0.1);
  transform: translateX(-3px);
}

.practice-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 15px;
}

.lang-toggle {
  border-radius: 20px;
  overflow: hidden;
}

.back-amc-btn {
  color: #667eea;
  font-weight: 600;
}

/* 加载和错误状态 */
.loading,
.error,
.empty {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.loading-inner {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  color: #667eea;
}

.error-box,
.empty-box {
  text-align: center;
  background: white;
  padding: 40px;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.error-box i,
.empty-box i {
  font-size: 48px;
  color: #e6a23c;
  margin-bottom: 15px;
}

.error-box p,
.empty-box p {
  font-size: 16px;
  color: #666;
  margin: 15px 0;
}

/* 内容区域 */
.content {
  max-width: 1400px;
  margin: 0 auto;
}

/* 统计卡片 */
.stats-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 25px;
}

.stat-card {
  background: white;
  border-radius: 15px;
  padding: 25px;
  text-align: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.2);
}

.stat-number {
  font-size: 32px;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 14px;
  color: #888;
  font-weight: 500;
}

/* 题目卡片 */
.question-card {
  background: white;
  border-radius: 20px;
  padding: 35px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  animation: fadeInUp 0.5s ease;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.q-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f0f0;
}

.q-title {
  font-size: 20px;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.q-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.q-content {
  font-size: 18px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9ff;
  border-radius: 12px;
  border-left: 4px solid #667eea;
  white-space: pre-wrap;
}

/* 题目图片样式 */
.q-images {
  margin: 20px 0 30px 0;
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: center;
}

.question-image {
  max-width: 100%;
  max-height: 500px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
}

.question-image:hover {
  transform: scale(1.02);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.2);
}

/* 选项样式 */
.q-options {
  margin-bottom: 30px;
}

.opt {
  display: block;
  margin-bottom: 15px;
}

.option-content {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  line-height: 1.6;
}

.option-label {
  font-weight: 600;
  color: #667eea;
  flex: 0 0 24px;
  width: 24px;
  display: inline-flex;
  justify-content: flex-start;
  align-items: flex-start;
  padding-top: 1px;
}

.option-content > span:not(.option-label) {
  flex: 1;
  min-width: 0;
  word-break: break-word;
}

.option-content .option-image {
  flex: 1;
  max-width: calc(100% - 24px);
}

.option-image {
  max-width: 100%;
  max-height: 200px;
  margin-top: 8px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.option-image:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.opt >>> .el-radio {
  width: 100%;
  padding: 18px 20px;
  margin: 0;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.opt >>> .el-radio:hover {
  border-color: #667eea;
  background: #f8f9ff;
  transform: translateX(5px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.15);
}

.opt >>> .el-radio.is-checked {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: #667eea;
  color: white;
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);
}

.opt >>> .el-radio.is-checked .el-radio__label {
  color: white !important;
}

.opt >>> .el-radio__label {
  display: block;
  white-space: normal;
}

/* 答案输入框 */
.q-answer-input {
  margin-bottom: 25px;
}

/* 答题状态 */
.answer-status {
  margin: 20px 0;
  padding: 15px 20px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
}

.ok {
  background: linear-gradient(135deg, #67c23a 0%, #85ce61 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(103, 194, 58, 0.3);
}

.ok i {
  font-size: 20px;
}

.bad {
  background: linear-gradient(135deg, #f56c6c 0%, #f78989 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(245, 108, 108, 0.3);
}

.bad i {
  font-size: 20px;
}

/* 解析区域 */
.analysis {
  margin-top: 25px;
  padding: 20px;
  background: linear-gradient(135deg, #f8f9ff 0%, #e8eaf6 100%);
  border-radius: 12px;
  border-left: 4px solid #667eea;
}

.a-title {
  font-size: 18px;
  font-weight: 700;
  color: #667eea;
  margin-bottom: 12px;
}

.a-content {
  line-height: 1.8;
  color: #333;
}

/* 操作按钮 */
.actions {
  margin-top: 30px;
  display: flex;
  gap: 15px;
  justify-content: center;
}

.actions >>> .el-button {
  padding: 12px 30px;
  font-size: 16px;
  border-radius: 25px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.actions >>> .el-button--primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.actions >>> .el-button--primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
}
</style>
